import {
  textGray600,
  textNegativeText,
  textPositiveText,
} from '@osmosis/tailwind-ui/styles/text';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P, H2, H3 } from '@osmosis/tailwind-ui/components/Text';

import React, { useEffect, useMemo, useState } from 'react';

import Divider from '../Divider';

import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons/faCircleXmark';
import { useQuizContext } from '../../context/QuizContext';
import {
  bg200,
  bgNegative,
  bgNegativeLight,
  bgPositive,
  bgPositiveLight,
  bgSlate400,
} from '@osmosis/tailwind-ui/styles/background';
import { dailyPracticeMessages } from '../../constants/quiz';
import { randomIntFromInterval } from '@osmosis/shared/number';

const getRandomWord = (words: string[]) => {
  if (words.length === 0) return '';
  const randomIndex = randomIntFromInterval(0, words.length - 1);
  return words[randomIndex];
};

const getTranslatedCorrectWords = (intl: IntlShape) => [
  intl.formatMessage(dailyPracticeMessages.correctWordCorrect),
  intl.formatMessage(dailyPracticeMessages.correctWordNice),
  intl.formatMessage(dailyPracticeMessages.correctWordAwesome),
  intl.formatMessage(dailyPracticeMessages.correctWordExcellent),
  intl.formatMessage(dailyPracticeMessages.correctWordGreat),
  intl.formatMessage(dailyPracticeMessages.correctWordNiceJob),
  intl.formatMessage(dailyPracticeMessages.correctWordNiceWork),
  intl.formatMessage(dailyPracticeMessages.correctWordWoot),
];

const getTranslatedIncorrectWords = (intl: IntlShape) => [
  intl.formatMessage(dailyPracticeMessages.incorrectWordShoot),
  intl.formatMessage(dailyPracticeMessages.incorrectWordDang),
  intl.formatMessage(dailyPracticeMessages.incorrectWordDarn),
  intl.formatMessage(dailyPracticeMessages.incorrectWordWhoops),
  intl.formatMessage(dailyPracticeMessages.incorrectWordMissedIt),
];

type DailyPracticeXPTabProps = {
  handleClick: (num: number) => void;
};

/**
 * DailyPracticeXPTab.
 */
const DailyPracticeXPTab = (props: DailyPracticeXPTabProps) => {
  const { handleClick: changeItem } = props;
  const [randWord, setRandWord] = useState('');
  const { xp, currentResponseItem, isAnswered, isDailyPractice } =
    useQuizContext();
  const isCorrect = currentResponseItem?.response?.accuracy;
  const intl = useIntl();

  const CORRECT_WORDS = useMemo(() => getTranslatedCorrectWords(intl), [intl]);
  const INCORRECT_WORDS = useMemo(
    () => getTranslatedIncorrectWords(intl),
    [intl],
  );

  useEffect(() => {
    const words = isCorrect ? CORRECT_WORDS : INCORRECT_WORDS;
    setRandWord(getRandomWord(words));
  }, [CORRECT_WORDS, INCORRECT_WORDS, isCorrect]);

  let tabBg = bg200;
  let buttonBg = bgNegative;
  let textColor = textNegativeText;

  if (isAnswered) {
    if (isCorrect) {
      tabBg = bgPositiveLight;
      buttonBg = bgPositive;
      textColor = textPositiveText;
    } else {
      tabBg = bgNegativeLight;
      buttonBg = bgNegative;
      textColor = textNegativeText;
    }
  }

  return (
    <Box
      className={isDailyPractice ? 'block' : 'hidden'}
      data-testid="dailyPracticeXPTab.wrapper"
    >
      <Divider cn={`${bgSlate400} h-[2px]`} />
      <Box className={`w-full h-[112px] px-16 ${tabBg}`}>
        <Flex
          className={`items-center h-[inherit] ${
            isCorrect ? 'justify-evenly' : 'justify-between'
          }`}
        >
          <Box
            className={`w-[62px] h-[42px] rounded-[5px] border border-gray-400 ${
              isAnswered ? 'ml-0' : 'ml-7'
            }`}
          >
            <Flex className={`h-[inherit] justify-center items-center`}>
              <H3 className={`${textGray600}`}>
                <FormattedMessage
                  id="dailyPracticeXPTab.xp-counter"
                  defaultMessage="{xpCounter} XP"
                  values={{ xpCounter: xp.total }}
                />
              </H3>
            </Flex>
          </Box>

          <Box className={isAnswered ? 'block' : 'hidden'}>
            <Flex>
              <Box className={`w-[40px] h-[40px] mr-2`}>
                <Flex className={`justify-center items-center h-[inherit]`}>
                  <Icon
                    icon={isCorrect ? faCheckCircle : faCircleXmark}
                    className={`text-[2em] ${textColor}`}
                  />
                </Flex>
              </Box>
              <Box>
                <H2 className={`${textColor}`}>{randWord}</H2>
                <P className={`${textColor}`}>
                  <FormattedMessage
                    id="dailyPracticeXPTab.continue.press_space"
                    defaultMessage="Press space to continue"
                  />
                </P>
              </Box>
            </Flex>
          </Box>

          <Box
            data-testid="dailyPracticeXPTab.continue-btn-wrapper"
            className={isAnswered ? 'block' : 'hidden'}
          >
            <Button
              data-testid="dailyPracticeXPTab.continue-btn"
              onClick={() => changeItem(1)}
              className={`text-white ${buttonBg} hover:${buttonBg} active:${buttonBg}`}
            >
              <FormattedMessage
                id="dailyPracticeXPTab.continue"
                defaultMessage="Continue"
              />
            </Button>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default DailyPracticeXPTab;
